import type { FeatureLike } from 'ol/Feature'
import { Circle, Fill, Stroke, Style } from 'ol/style'
import type { GeometryCollection, SimpleGeometry } from 'ol/geom'
import { LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom'
import type { Type as GeometryType } from 'ol/geom/Geometry'
import { Feature } from 'ol'
import type { GeoJSONGeometry } from 'ol/format/GeoJSON'
import type { Options as StyleOptions } from 'ol/style/Style'
import { fromString as colorFromString } from 'ol/color'
import FontSymbol from 'ol-ext/style/FontSymbol'
import { loadFontAwesomeDefs, loadMaki2Defs, loadMakiDefs } from '../utils/map/FontSymbolDefs'
import VectorStyleData = Datakode.LaravelDatakodeUtils.LaravelData.VectorStyle.VectorStyleData
import GeometrySubtypeEnum = Datakode.LaravelDatakodeUtils.Enum.GeometrySubtypeEnum
import FillData = Datakode.LaravelDatakodeUtils.LaravelData.VectorStyle.FillData
import StrokeData = Datakode.LaravelDatakodeUtils.LaravelData.VectorStyle.StrokeData
import FontSymbolData = Datakode.LaravelDatakodeUtils.LaravelData.VectorStyle.FontSymbolData
import FontSymbolFormEnum = Datakode.LaravelDatakodeUtils.Enum.FontSymbolFormEnum

// noinspection JSUnusedGlobalSymbols
export class VectorLayerService {
    protected highlightedFeatures: FeatureLike[] = []
    protected selectedFeatures: FeatureLike[] = []

    protected highlightOnlyFirst: boolean = false
    protected hideSelected: boolean = false

    setHighlightedFeatures(highlightedFeatures: FeatureLike[]): void {
        this.highlightedFeatures = highlightedFeatures
        if (this.highlightOnlyFirst) {
            this.highlightedFeatures = this.highlightedFeatures.slice(0, 1)
        }
    }

    setSelectedFeatures(selectedFeatures: FeatureLike[] | null): void {
        this.selectedFeatures = selectedFeatures ? selectedFeatures : []
    }

    isFeatureIn(needle: FeatureLike, haystack: FeatureLike[]): boolean {
        return haystack.filter((candidate) => {
            return candidate.ol_uid === needle.ol_uid
                || (needle.get('k') && candidate.get('k') === needle.get('k')) // because ol_uid change on layer reload
        }).length > 0
    }

    isHighlighted(feature: FeatureLike): boolean {
        return this.isFeatureIn(feature, this.highlightedFeatures)
    }

    isSelected(feature: FeatureLike): boolean {
        return this.isFeatureIn(feature, this.selectedFeatures)
    }

    setHideSelected(hideSelected: boolean): void {
        this.hideSelected = hideSelected
    }

    isEditing(): boolean {
        return this.hideSelected
    }

    getEditedFeatureStyle(feature: FeatureLike, currentStyle: Style, resolution: number): Style {
        const geomType = feature.getGeometry().getType()

        const isHighLighted = this.isHighlighted(feature)

        // override style in child service
        switch (geomType) {
            case 'Point' :
            case 'MultiPoint' :
                return this.getEditedPointStyle(feature, isHighLighted, resolution)
            case 'LineString' :
            case 'MultiLineString' :
                return this.getEditedLineStringStyle(feature, isHighLighted, resolution)
            case 'Polygon' :
            case 'MultiPolygon' :
                return this.getEditedPolygonStyle(feature, isHighLighted, resolution)
            default:
                console.warn(`Not handled geom type ${geomType}`)
                console.warn(feature)
                return new Style(null)
                // throw new Error(`Not handled geom type ${geomType}`)
        }
    }

    getFeatureStyle(feature: FeatureLike, currentStyle: Style, resolution: number): Style {
        const geomType = feature.getGeometry().getType()
        const isHighLighted = this.isHighlighted(feature)
        const isSelected = this.isSelected(feature)

        if (isSelected && this.hideSelected) {
            return new Style(null) // hidden
        }

        // override style in child service
        switch (geomType) {
            case 'Point' :
            case 'MultiPoint' :
                return this.getPointStyle(feature, isHighLighted, isSelected, resolution)
            case 'LineString' :
            case 'MultiLineString' :
                return this.getLineStringStyle(feature, isHighLighted, isSelected, resolution)
            case 'Polygon' :
            case 'MultiPolygon' :
                return this.getPolygonStyle(feature, isHighLighted, isSelected, resolution)
            default:
                // Need to handle GeometryCollection from serpic when there are wrong geometries
                console.warn(`Not handled geom type ${geomType}`)
                console.warn(feature)
                return new Style(null)
                // throw new Error(`Not handled geom type ${geomType}`)
        }
    }

    // eslint-disable-next-line unused-imports/no-unused-vars
    getPointStyle(feature: FeatureLike, isHighLighted: boolean, isSelected: boolean, resolution: number): Style {
        // exemple style Point
        return new Style({
            image: new Circle({
                radius: isSelected || isHighLighted ? 12 : 8,
                fill: new Fill({
                    color: isHighLighted ? '#ff5100ff' : '#3db93dff',
                }),
                stroke: new Stroke({
                    color: '#000000ff',
                    width: 1,
                }),
            }),
        })
    }

    getEditedPointStyle(feature: FeatureLike, isHighLighted: boolean, resolution: number): Style {
        const res = this.getPointStyle(feature, false, false, resolution)
        const image = res.getImage()
        if (image) {
            image.setOpacity(0.5)
        }
        res.getImage().setFill(new Fill({
            color: '#0098ffff',
        }))
        return res
    }

    // eslint-disable-next-line unused-imports/no-unused-vars
    getLineStringStyle(feature: FeatureLike, isHighLighted: boolean, isSelected: boolean, resolution: number): Style {
        // exemple style Point
        return new Style({
            stroke: new Stroke({
                color: isHighLighted || isSelected ? '#ff5100ff' : '#3db93dff',
                width: 3,
            }),
        })
    }

    getEditedLineStringStyle(feature: FeatureLike, isHighLighted: boolean, resolution: number): Style {
        const res = this.getLineStringStyle(feature, false, false, resolution)
        res.setStroke(new Stroke({
            color: '#0098ff',
        }))
        return res
    }

    // eslint-disable-next-line unused-imports/no-unused-vars
    getPolygonStyle(feature: FeatureLike, isHighLighted: boolean, isSelected: boolean, resolution: number): Style {
        // exemple style Point
        return new Style({
            fill: new Fill({
                color: isHighLighted || isSelected ? '#ff5100ff' : '#3db93dff',
            }),
            stroke: new Stroke({
                color: '#000000ff',
                width: 1,
            }),
        })
    }

    getEditedPolygonStyle(feature: FeatureLike, isHighLighted: boolean, resolution: number): Style {
        const res = this.getPolygonStyle(feature, false, false, resolution)
        res.setFill(new Fill({
            color: '#0098FF66',
        }))
        return res
    }

    saveFeatureOriginalGeometryType(feature: FeatureLike): void {
        const geometry = feature.getGeometry() as SimpleGeometry
        // save original geometry Type
        feature.set('_originalGeometryType', geometry.getType())
        feature.set('_originalGeometryTypeCoordinates', geometry.getCoordinates())
    }

    getGeometryFromOriginalGeometryTypeProperties(originalFeature: FeatureLike): SimpleGeometry {
        const coordinates = originalFeature.get('_originalGeometryTypeCoordinates')
        switch (originalFeature.get('_originalGeometryType') as GeometryType) {
            case 'Point':
                return new Point(coordinates)
            case 'MultiPoint':
                return new MultiPoint(coordinates)
            case 'LineString':
                return new LineString(coordinates)
            case 'MultiLineString':
                return new MultiLineString(coordinates)
            case 'Polygon':
                return new Polygon(coordinates)
            case 'MultiPolygon':
                return new MultiPolygon(coordinates)
        }
    }

    cloneAndExplodeFeature(originalFeature: FeatureLike): FeatureLike[] {
        let finalFeatures = [] as FeatureLike[]
        let originalGeometry: SimpleGeometry = originalFeature.getGeometry()

        if (originalFeature.get('_originalGeometryType')) {
            originalGeometry = this.getGeometryFromOriginalGeometryTypeProperties(originalFeature)
        }

        switch (originalGeometry.getType() as GeometryType) {
            case 'MultiPoint':
                (originalGeometry as MultiPoint).getPoints().forEach((g: Point) => {
                    finalFeatures = [...finalFeatures, ...this.cloneAndExplodeFeature(new Feature(g))]
                })
                break

            case 'MultiLineString':
                (originalGeometry as MultiLineString).getLineStrings().forEach((g: LineString) => {
                    finalFeatures = [...finalFeatures, ...this.cloneAndExplodeFeature(new Feature(g))]
                })
                break

            case 'MultiPolygon':
                (originalGeometry as MultiPolygon).getPolygons().forEach((g: Polygon) => {
                    finalFeatures = [...finalFeatures, ...this.cloneAndExplodeFeature(new Feature(g))]
                })
                break
            case 'GeometryCollection':
                (originalGeometry as GeometryCollection).getGeometries().forEach((g: SimpleGeometry) => {
                    finalFeatures = [...finalFeatures, ...this.cloneAndExplodeFeature(new Feature(g))]
                })
                break
            default:
                finalFeatures.push(new Feature(originalGeometry.clone()))
        }

        return finalFeatures
    }

    forceMultiGeoJSONGeometry(geometry: GeoJSONGeometry): GeoJSONGeometry {
        if (geometry.type.startsWith('Multi')) {
            return geometry
        }
        return {
            type: `Multi${geometry.type}`,
            coordinates: [geometry.coordinates],
        } as GeoJSONGeometry
    }

    getMultiGeomBasicGeometries(geometry: SimpleGeometry): SimpleGeometry[] {
        if (geometry) {
            switch (geometry.getType() as GeometryType) {
                case 'MultiPoint':
                    return (geometry as MultiPoint).getPoints()
                case 'MultiLineString':
                    return (geometry as MultiLineString).getLineStrings()
                case 'MultiPolygon':
                    return (geometry as MultiPolygon).getPolygons()
                case 'GeometryCollection':
                    return (geometry as GeometryCollection).getGeometries()
                default:
                    return [geometry]
            }
        }
        return
    }

    loadFontSymbolDefs(): void {
        loadFontAwesomeDefs()
        loadMakiDefs()
        loadMaki2Defs()
        // add other font if needed see utils/map/FontSymbolDefs.ts
    }

    getStyle(feature: FeatureLike, isHighLighted: boolean, isSelected: boolean, resolution: number, geometrySubtype: GeometrySubtypeEnum): Style {
        switch (geometrySubtype) {
            case 'Point':
                return this.getPointStyle(feature, isHighLighted, isSelected, resolution)
            case 'LineString':
                return this.getLineStringStyle(feature, isHighLighted, isSelected, resolution)
            case 'Polygon':
                return this.getPolygonStyle(feature, isHighLighted, isSelected, resolution)
        }
        return this.getPointStyle(feature, isHighLighted, isSelected, resolution)
    }

    public getStyleOptionsFromJson(json: VectorStyleData | null, isHighLighted: boolean = false, isSelected: boolean = false): StyleOptions | null {
        if (!json) {
            return null
        }
        const fillData = json.fill as FillData | null
        const strokeData = json.stroke as StrokeData | null
        const fontSymbolData = json.fontSymbol as FontSymbolData | null
        const options: StyleOptions = {}

        if (fillData) {
            const fillColor = colorFromString(
                isHighLighted
                    ? this.fillHighLightedColor
                    : isSelected
                        ? this.fillSelectedColor
                        : fillData.color,
            )
            options.fill = new Fill({
                color: fillColor,
            })
        }

        if (strokeData) {
            const strokeColor = colorFromString(
                isHighLighted
                    ? this.strokeHighLightedColor
                    : isSelected
                        ? this.strokeSelectedColor
                        : strokeData.color,
            )
            options.stroke = new Stroke({
                color: strokeColor,
                width: Number.parseFloat(strokeData.width),
                lineCap: strokeData.lineCap,
                lineJoin: strokeData.lineJoin,
                miterLimit: Number.parseFloat(strokeData.miterLimit),
            })
        }

        if (fontSymbolData) {
            const fillColor = colorFromString(
                isHighLighted
                    ? this.fillHighLightedColor
                    : isSelected
                        ? this.fillSelectedColor
                        : fontSymbolData.color,
            )
            if (fontSymbolData.displacement === null) {
                switch (fontSymbolData.form as FontSymbolFormEnum) {
                    case 'none':
                    case 'circle':
                    case 'bookmark':
                    case 'hexagon':
                    case 'diamond':
                    case 'triangle':
                    case 'sign':
                    case 'ban':
                    case 'lozenge':
                    case 'square':
                        fontSymbolData.displacement = [0, 0]
                        break

                    case 'poi':
                    case 'bubble':
                    case 'marker':
                    case 'coma':
                    case 'shield':
                    case 'blazon':
                        fontSymbolData.displacement = [0, fontSymbolData.radius]
                        break
                }
            }

            options.image = new FontSymbol({
                color: fillColor,
                fill: new Fill({
                    color: fontSymbolData.fillColor,
                }),
                stroke: new Stroke({
                    color: fontSymbolData.strokeColor,
                    width: Number.parseFloat(fontSymbolData.strokeWidth),
                }),
                glyph: fontSymbolData.glyph,
                form: fontSymbolData.form,
                radius: Number.parseFloat(fontSymbolData.radius),
                fontSize: Number.parseFloat(fontSymbolData.fontSize),
                displacement: fontSymbolData.displacement,
            })
        }

        return options
    }
}
